import moment from "moment";

import { DateFormats } from "@enums/shared.enum";
import { ResourcePricesResponse } from "@models/components/booking.model";
import {
  AvailableTiming,
  BookingSliceInitial,
  SingleSlot,
  timeMapObj,
} from "@models/services/booking";
import { AddGuestType } from "@models/shared/shared.model";
import { CaseReducer, createSlice, PayloadAction } from "@reduxjs/toolkit";

import drawerSlice from "./drawer.slice";

const initialState: BookingSliceInitial = {
  calenderStartDate: moment().format(DateFormats.YMD),
  checkparticipantErrors: {},
  gdprAccepted: false,
  guestList: [],
  isSingleTimeSlot: false,
  legalTermsAccepted: false,
  paymentLoader: false,
  resourcePrices: {} as ResourcePricesResponse,
  selectedDate: moment().format(DateFormats.YMD),
  selectedSlot: {} as SingleSlot,
  selectedTime: "",
  timeMaps: [],
};

type bookingSliceReducer<Payload> = CaseReducer<
  BookingSliceInitial,
  PayloadAction<Payload>
>;

const changeCalenderStartDate: bookingSliceReducer<string> = (
  state,
  action
) => {
  state.calenderStartDate = action.payload;
};

const changeSelectedDate: bookingSliceReducer<string> = (state, action) => {
  state.selectedDate = action.payload;
  state.selectedSlot = {} as SingleSlot;
  state.selectedTime = "";
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
  state.resourcePrices = {} as ResourcePricesResponse;
};

const updateTimeMaps: bookingSliceReducer<Array<Array<timeMapObj>>> = (
  state,
  action
) => {
  state.timeMaps = action.payload;
};

const changeIsSingleTimeSlot: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.isSingleTimeSlot = action.payload;
};

const acceptGdpr: bookingSliceReducer<boolean> = (state, action) => {
  state.gdprAccepted = action.payload;
};

const acceptLegalTerms: bookingSliceReducer<boolean> = (state, action) => {
  state.legalTermsAccepted = action.payload;
};

const changeSelectedTime: bookingSliceReducer<string> = (state, action) => {
  state.selectedSlot = {} as SingleSlot;
  state.selectedTime = action.payload;
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
};

const changeSelectedSlot: bookingSliceReducer<{
  slot: AvailableTiming;
  resourceId: string;
  isBookable: boolean;
}> = (state, action) => {
  state.selectedSlot = action.payload;
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
};
const setCheckParticipantErrors: bookingSliceReducer<Record<string, string>> = (
  state,
  action
) => {
  state.checkparticipantErrors = action.payload;
};

const setResourcePrices: bookingSliceReducer<ResourcePricesResponse> = (
  state,
  action
) => {
  state.resourcePrices = action.payload;
};

const flush: bookingSliceReducer<null> = state => {
  state.selectedDate = moment().format(DateFormats.YMD);
  state.selectedSlot = {} as SingleSlot;
  state.selectedTime = "";
  state.calenderStartDate = moment().format(DateFormats.YMD);
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
  state.resourcePrices = {} as ResourcePricesResponse;
};

const setPaymentLoader: bookingSliceReducer<boolean> = (state, action) => {
  state.paymentLoader = action.payload;
};
const addGuest: bookingSliceReducer<AddGuestType> = (state, action) => {
  const exists = state.guestList.some(
    p => p.profileUuid === action.payload.profileUuid
  );
  if (!exists) state.guestList.push(action.payload);
};

const removeGuest: bookingSliceReducer<string> = (state, action) => {
  state.guestList = state.guestList.filter(
    p => p.profileUuid !== action.payload
  );
};

const setGuests: bookingSliceReducer<AddGuestType[]> = (state, action) => {
  state.guestList = action.payload;
};

const bookingSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(drawerSlice.actions.removeDrawerBookingState, state => {
      state.selectedTime = "";
      state.selectedSlot = {} as SingleSlot;

      if (state.guestList.length > 0) {
        state.guestList = [];
      }
    });
  },
  initialState,
  name: "booking",
  reducers: {
    acceptGdpr,
    acceptLegalTerms,
    addGuest,
    changeCalenderStartDate,
    changeIsSingleTimeSlot,
    changeSelectedDate,
    changeSelectedSlot,
    changeSelectedTime,
    flush,
    removeGuest,
    setCheckParticipantErrors,
    setGuests,
    setPaymentLoader,
    setResourcePrices,
    updateTimeMaps,
  },
});

export default bookingSlice;
